import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Space, Row, Col, Result, message } from "antd";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  PDFDownloadLink,
} from "@react-pdf/renderer";

import { useStepsForm } from "sunflower-antd";
import dayjs from "dayjs";
import { formatTransactionDate } from "../../../utils/sastDate";


interface IModalProps {
  show: boolean;
  member: any;
  payments: any;
  showModal: Function;
  accountBalance: any;
}

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#fff",
    padding: 10,
  },
  section: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
  },
  heading: {
    fontSize: 16,
    textAlign: "center",
    marginBottom: 10,
  },
  label: {
    fontWeight: "bold",
  },
  value: {
    marginLeft: 5,
  },
  logo: {
    width: "25%",
    textAlign: "center",
  },
  container: {
    border: "1px solid black",
    width: "100%",
    marginBottom: "16px",
    padding: "6px",
  },
  row: {
    flexDirection: "row",
  },
  column: {
    flex: 1,
    padding: "6px",
  },
  table: {
    width: "100%",
    textAlign: "left",
  },
});

const StatementModal: React.FC<IModalProps> = ({
  show,
  member,
  payments,
  showModal,
  accountBalance,
}) => {

  const [date, setDate] = useState("");

  const handleModal = () => {
    showModal();
    gotoStep(0);
    form.resetFields();
  };

  const handleDownload = () => {
    setTimeout(() => {
      showModal();
      gotoStep(0);
      message.success("Document has started downloading.");
    }, 100);
  };

  useEffect(() => {
    const localeDate = new Date();
    const monthIndex = localeDate.getMonth();
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const monthName = monthNames[monthIndex];
    const year = localeDate.getFullYear();

    setDate(`${monthName} ${year}`);
  }, []);

  const {
    form,
    current,
    gotoStep,
    stepsProps,
    formProps,
    submit,
    formLoading,
  } = useStepsForm({
    isBackValidate: false,
    async submit(values: any) {
      console.log("Printing Statement");
    },
    total: 2,
  });

  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 8 },
  };

  const tailLayout = {
    labelCol: { span: 0 },
    wrapperCol: { offset: 8, span: 8 },
  };



  const MyDocument = () => (
    <Document>
      <Page size="A4" style={{ padding: "16px" }}>
        <View>
          <View>
            {member.Group?.CompanyLogo && (
              <Image style={styles.logo} src={member.Group.CompanyLogo} cache={false} />
            )}
            <Text style={styles.heading}>Account Statement</Text>
          </View>

          <View
            style={{
              border: "1px solid black",
              padding: "16px",
              marginBottom: "16px",
              width: "100%",
              flexDirection: "row",
              fontSize: "10px",
            }}
          >
            <View style={{ width: "50%" }}>
              <Text>
                {member.Name} {member.Surname}
              </Text>
              <Text>{member.AddressLine1}</Text>
              <Text>{member.AddressLine2}</Text>
              <Text>{member.Province}</Text>
            </View>

            <View style={{ width: "50%" }}>
              <Text>{member.Cellphone}</Text>
              <Text>{member.Email}</Text>
              <Text>{member.CityTown}</Text>
              <Text>{member.PostalCode}</Text>
            </View>
          </View>

          <View
            style={{
              border: "1px solid black",
              padding: "16px",
              marginBottom: "16px",
              width: "100%",
              flexDirection: "row",
              fontSize: "10px",
            }}
          >
            <View style={{ width: "20%" }}>
              <Text style={{ fontWeight: "bold", marginBottom: "6px" }}>
                Payment Date
              </Text>
              {payments.map((payment: any, index: any) => (
                <Text key={index}>
                  {formatTransactionDate(payment.Date)}
                </Text>
              ))}
            </View>
            <View style={{ width: "20%" }}>
              <Text style={{ marginBottom: "6px" }}>Payment Type</Text>
              {payments.map((payment: any, index: any) => (
                <Text key={index}>{payment.Type}</Text>
              ))}
            </View>
            <View style={{ width: "20%" }}>
              <Text style={{ marginBottom: "6px" }}>Payment Amount</Text>
              {payments.map((payment: any, index: any) => (
                <Text key={index}>R {payment.Amount}</Text>
              ))}
            </View>
            <View style={{ width: "20%" }}>
              <Text style={{ marginBottom: "6px" }}>Payment Reference</Text>
              {payments.map((payment: any, index: any) => (
                <Text key={index}>{payment.Reference}</Text>
              ))}
            </View>
            <View style={{ width: "20%" }}>
              <Text style={{ marginBottom: "6px" }}>Payment Balance</Text>
              {payments.map((payment: any, index: any) => {
                let balance = payments
                  .slice(0, index + 1)
                  .reduce(
                    (acc: any, curr: any) => acc + parseFloat(curr.Amount),
                    0
                  );
                return <Text key={index}>R {balance}</Text>;
              })}
            </View>
          </View>

          <View
            style={{
              border: "1px solid black",
              padding: "16px",
              marginBottom: "16px",
              width: "100%",
              flexDirection: "row",
              fontSize: "10px",
            }}
          >
            <View style={{ width: "50%" }}>
              <Text>{date}</Text>
              <Text>Account Balance</Text>
            </View>
            <View style={{ width: "50%", textAlign: "right" }}>
              <Text>R {accountBalance}</Text>
              <Text>R {accountBalance}</Text>
            </View>
          </View>

          <View style={{ fontSize: "10px", textAlign: "center" }}>
            <Text>Thank you for your business.</Text>
          </View>
        </View>
      </Page>
    </Document>
  );

  const formList: any = [
    <>
      <div style={{ textAlign: "center" }}>
        {member.Group?.CompanyLogo && (
          <img src={member.Group.CompanyLogo} alt="Logo" width={"25%"} />
        )}
        <h3>Account Statement</h3>
      </div>

      <Row
        style={{
          border: "1px solid black",
          width: "100%",
          marginBottom: "16px",
          padding: "6px",
        }}
      >
        {" "}
        <Col span={12}>
          <p>
            <strong>
              {member.Name} {member.Surname}
            </strong>
          </p>
          <p>{member.AddressLine1}</p>
          <p>{member.AddressLine2}</p>
          <p>{member.Province}</p>
        </Col>
        <Col>
          <p>{member.Cellphone}</p>
          <p>{member.Email}</p>
          <p>{member.CityTown}</p>
          <p>{member.PostalCode}</p>
        </Col>
      </Row>

      <Row
        style={{
          border: "1px solid black",
          width: "100%",
          marginBottom: "16px",
          padding: "6px",
        }}
      >
        <table style={{ width: "100%", textAlign: "left" }}>
          <thead>
            <tr>
              <th>Payment Date</th>
              <th>Payment Type</th>
              <th>Payment Amount</th>
              <th>Payment Reference</th>
              <th>Payment Balance</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((payment: any, index: number) => {
              // Calculate balance
              let balance = 0;
              for (let i = 0; i <= index; i++) {
                balance += Number(payments[i].Amount);
              }
              return (
                <tr key={payment.id}>
                  <td>{formatTransactionDate(payment.Date)}</td>
                  <td>{payment.Type}</td>
                  <td>R {payment.Amount}</td>
                  <td>{payment.Reference}</td>
                  <td>R {balance}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Row>

      <Row
        style={{
          border: "1px solid black",
          width: "100%",
          marginBottom: "16px",
          padding: "6px",
        }}
      >
        {" "}
        <Col span={20}>
          <p>{date}</p>
          <p>
            <strong>Account Balance</strong>
          </p>
        </Col>
        <Col>
          <p>R {accountBalance}</p>
          <p>
            <strong>R {accountBalance}</strong>
          </p>
        </Col>
      </Row>

      <div style={{ textAlign: "center", marginBottom: "16px" }}>
        {/* <p>
          Lorem ipsum dolor sit amet. Et vero sint est aspernatur distinctio nam
          illum aliquam. Ad delectus illo sed eveniet recusandae eos expedita
          repellat qui assumenda galisum.
        </p>
        <p>
          Non quia quaerat nam galisum dolores id tenetur sunt sit voluptas
          nihil et Quis earum. Est accusamus voluptas et officia consequatur ab
          maxime odit.
        </p> */}
        <h4>Thank you for your business.</h4>
      </div>

      <Form.Item {...tailLayout}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <PDFDownloadLink
            document={<MyDocument />}
            fileName={member.Name + " " + member.Surname + "_Statement"}
            style={{ marginBottom: 10 }}
          >
            <Button block size="large" onClick={handleDownload}>
              Download Statement
            </Button>
          </PDFDownloadLink>
        </Space>
      </Form.Item>
    </>,
  ];

  return (
    <Modal
      title="Monthly Statement"
      open={show}
      onOk={handleModal}
      onCancel={handleModal}
      width={900}
      footer={null}
    >
      {/* Steps Form */}
      <div>
        <div style={{ marginTop: 60 }}>
          <Form {...layout} {...formProps} style={{ maxWidth: 900 }}>
            {formList[current]}
          </Form>

          {current === 1 && (
            <Result status="success" title="Successfully submitted.">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleModal}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}

          {current === 2 && (
            <Result status="error" title="Error occurred, please try again.">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleModal}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default StatementModal;
